import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'

const SidebarContext = createContext(undefined)

export const useSidebar = () => {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}

export const SidebarProvider = ({ children }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  const [isCollapsed, setIsCollapsed] = useState(() => sessionStorage.getItem('sidebarCollapsed') === 'true')

  useEffect(() => {
    let timeoutId
    const handleResize = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        const width = window.innerWidth
        setIsMobile(width < 768)
        if (width >= 1280) setIsMobileOpen(false)
      }, 150)
    }
    window.addEventListener('resize', handleResize)
    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const toggleMobileSidebar = useCallback(() => setIsMobileOpen(prev => !prev), [])
  const closeMobileSidebar = useCallback(() => setIsMobileOpen(false), [])
  const toggleCollapse = useCallback(() => setIsCollapsed(prev => {
    const next = !prev
    sessionStorage.setItem('sidebarCollapsed', String(next))
    return next
  }), [])

  const value = useMemo(() => ({
    isMobileOpen, toggleMobileSidebar, closeMobileSidebar, isMobile, isCollapsed, toggleCollapse
  }), [isMobileOpen, toggleMobileSidebar, closeMobileSidebar, isMobile, isCollapsed, toggleCollapse])

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  )
}
